import React, { useCallback } from 'react';
import { useForm } from '~/hooks';
import { useSectionLoader } from '../../hooks';
import { useValidation } from '~/hooks/useValidation';
import sectionTypes from '../sectionTypes';
import { BsFillExclamationCircleFill } from 'react-icons/bs';

/**
 * @typedef PropertyInfoReviewValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId * 
 * @typedef FormState
 * @property {PropertyInfoReviewValues} values The current form values
 */

/** @type {PropertyInfoReviewValues} */

const defaultState = {
    sectionId: 0,
    review: false
};

const loadData = async (validate, values) => {
    let errorSection = false;
    if (validate) {
        for (const [key, value] of Object.entries(values)) {
            if (key !== 'additionalSection' && value === null || value === '') {
                errorSection = true;
                break;
            }
        }
    }
    return {
        ...values,
        errorSection: errorSection

    };
};

const validationSchema = {
    titleCompany: {},
    fileNumber: {},
    transactionType: {},
    useOfProperty: {},
    street: {},
    zipcode: {},
    city: {},
    county: {},
    state: {},
    interestOrEstate: {},
    notes: {
        validate: (field, data) => data.documents?.length > 0 || field?.length > 0
    }
};

const PropertyInfoReview = ({ titleOpinionId, validate, validationResult }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.PROPERTY_INFO, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(validate, values).then(v => loadSection(v));
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        //fieldActions
    } = useForm({
        /** @type {ReviewValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const fileNames = state.values && state.values.documents ? state.values.documents.map((entry) => {
        return entry.file ? entry.file.path : '';
    }) : [];

    let errorSection = false;
    if (validate) {
        if (!Object.keys(state.values).includes('fileNumber')
            || !Object.keys(state.values).includes('transactionType') || !Object.keys(state.values).includes('useOfProperty')
            || !Object.keys(state.values).includes('zipcode') || !Object.keys(state.values).includes('interestOrEstate')
            || !Object.keys(state.values).includes('street') || !Object.keys(state.values).includes('notes')) {
            errorSection = true;
        }
        if (!errorSection) {
            for (const [key, value] of Object.entries(state.values)) {
                // Legal description is not required if a copy of description or deed is uploaded.
                if (key === 'notes' && fileNames.length > 0) continue;

                if (key !== 'additionalSection' && key !== 'orderNumber' && key !== 'errorMessage' && key !== 'documents'
                    && value === null || value === ''
                    || (key === 'notes' && (value === null || value === ''))
                ) {
                    errorSection = true;
                    break;
                }
            }
        }
        validationResult(errorSection);
    }

    const [, invalidFields] = useValidation(state.values, validationSchema);

    return (

        <div className="ml-2 mr-5">
            {state.values.sectionId === 0 ?
                <>
                    <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4" />
                    <div>
                        Loading ...
                    </div>
                </>
                :
                <>
                    <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4" />
                    <div className="text-1xl text-blue-60 font-bold mb-3">
                        <div className="display:flex; flex-direction:row;">
                            <div className="z-10 flex flex-row h-full  text-xs font-semibold text-blue-60">
                                <div className="mt-1 mb-2">1. <a href={`/StraightFinal/${titleOpinionId}/propertyInfos`}>Property Information</a> </div>
                            </div>

                        </div>
                    </div>
                    <div className="col-span-8 grid grid-cols-8 grid-flow-row gap-x-8 gap-y-1  text-xs mb-4">
                        <div className="col-span-8 text-red-600">
                            {errorSection ?
                                <p className="flex flex-row gap-x-2 mb-2"><BsFillExclamationCircleFill />
                                    You have not provided all the required information in this section.</p>
                                : ''
                            }
                        </div>
                        <div className={`col-span-1 font-semibold ${invalidFields.titleCompany && 'text-red-600'}`}>Title Company: </div>
                        <div className="col-span-2 flex-end">{state.values.titleCompany ? state.values.titleCompany.label : state.errors.titleCompany}</div>
                        <span className="col-span-1" />
                        <div className={`col-span-2 font-semibold ${invalidFields.fileNumber && 'text-red-600'}`}>File No: </div>
                        <div className="col-span-2 flex-end">{state.values.fileNumber}</div>
                        <div className={`col-span-1 font-semibold ${invalidFields.transactionType && 'text-red-600'}`}>Transaction Type: </div>
                        <div className="col-span-2 flex-end">{state.values.transactionType ? state.values.transactionType.label : ''}{state.values.cashSale ? ', cash sale' : ''}</div>
                        <span className="col-span-5" />
                        <div className="col-span-1 font-semibold">Use of Property: </div>
                        <div className="col-span-2 flex-end">{state.values.useOfProperty ? state.values.useOfProperty.label : ''}</div>
                        <span className="col-span-1" />
                        <div className={`col-span-2 font-semibold ${invalidFields.street && 'text-red-600'}`}>Property Street: </div>
                        <div className="col-span-2 flex-end">{state.values.street}</div>
                        <div className={`col-span-1 font-semibold ${invalidFields.zipcode && 'text-red-600'}`}>Zip Code: </div>
                        <div className="col-span-2 flex-end">{state.values.zipcode}</div>
                        <span className="col-span-1" />
                        <div className={`col-span-2 font-semibold ${invalidFields.state && 'text-red-600'}`}>State: </div>
                        <div className="col-span-2 flex-end">{state.values.state ? state.values.state.value : ''}</div>
                        <div className={`col-span-1 font-semibold ${invalidFields.county && 'text-red-600'}`}>County: </div>
                        <div className="col-span-2 flex-end">{state.values.county}</div>
                        <span className="col-span-1" />
                        <div className={`col-span-2 font-semibold ${invalidFields.interestOrEstate && 'text-red-600'}`}>Interest or State: </div>
                        <div className="col-span-2 flex-end">{state.values.interestOrEstate ? state.values.interestOrEstate.label : ''}</div>
                        <div className={`col-span-1 font-semibold ${invalidFields.notes && 'text-red-600'}`}>Legal Description: </div>
                        <div className="col-span-7 flex-end">{state.values.notes}</div>

                    </div>
                </>
            }
        </div>
    );
};

export default PropertyInfoReview;